import { Checkbox, FormControlLabel, FormGroup, Icon, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { ChangeEvent, ReactElement, useCallback, useEffect, useRef, useState } from 'react';

import { MidFilter } from 'app/model/mid-filter';

export type FilterVersion = 'advanced' | 'basic';
export interface MLTrainingFilterType {
    onChange: (newFilter?: MidFilter, filterVersion?: FilterVersion, isNegative?: boolean) => void;
    isTagStore: boolean;
}

interface CheckboxesStateType {
    trained: boolean;
    notTrained: boolean;
    trainedAsTraining: boolean;
    trainedAsValidation: boolean;
    trainedAsTest: boolean;
}
const defaultFilterValue: MidFilter = {
    field: 'mlType',
    value: '',
    negate: false,
};
export const MLTrainingFilter: (props: MLTrainingFilterType) => ReactElement = ({ onChange, isTagStore }) => {
    const initial = useRef(true);
    const [version, setVersion] = useState<FilterVersion>('basic');
    const [checkboxesState, setCheckboxesState] = useState<CheckboxesStateType>({
        trained: true,
        notTrained: true,
        trainedAsTraining: true,
        trainedAsValidation: true,
        trainedAsTest: true,
    });
    const [isNegative, setisNegative] = useState(false);
    const handleChange = useCallback<(event: ChangeEvent<HTMLInputElement>) => void>((event) => {
        setCheckboxesState((oldState) => ({
            ...oldState,
            [event.target.name as keyof CheckboxesStateType]: event.target.checked,
        }));
    }, []);
    useEffect(() => {
        if (initial.current) {
            initial.current = false;
        } else {
            let newFilterValue = '';
            if ('basic' === version) {
                if (
                    (checkboxesState.trained && checkboxesState.notTrained) ||
                    (!checkboxesState.trained && !checkboxesState.notTrained)
                ) {
                    newFilterValue = '';
                } else if (checkboxesState.trained) {
                    newFilterValue = 'test|validation|training';
                } else if (checkboxesState.notTrained) {
                    newFilterValue = '.{0}';
                }
            } else {
                const newFilterArrValue: string[] = [];
                if (checkboxesState.trainedAsTraining) {
                    newFilterArrValue.push('training');
                }
                if (checkboxesState.trainedAsValidation) {
                    newFilterArrValue.push('validation');
                }
                if (checkboxesState.trainedAsTest) {
                    newFilterArrValue.push('test');
                }
                if (checkboxesState.notTrained) {
                    newFilterArrValue.push('.{0}');
                }
                // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                newFilterValue = 4 === newFilterArrValue.length ? '' : newFilterArrValue.join('|');
            }

            onChange(
                newFilterValue ? { ...defaultFilterValue, value: newFilterValue } : undefined,
                version,
                isNegative,
            );
        }
    }, [version, checkboxesState, onChange, isNegative]);

    return (
        <Stack direction='row'>
            <Stack direction='row' alignItems='center' justifyContent='center' height='42px' mr={2}>
                <Typography variant='body1' fontWeight={600} sx={{ display: 'flex', alignItems: 'center' }}>
                    Training Controls <Icon sx={{ ml: 1, mr: 0.5, fontSize: 'inherit !important' }}>info</Icon>:
                </Typography>
            </Stack>

            <Stack>
                <RadioGroup
                    row
                    value={version}
                    onChange={(event) => {
                        setVersion(event.target.value as FilterVersion);
                    }}
                >
                    <FormControlLabel value='basic' control={<Radio />} label='Show Basic (for taggers)' />
                    <FormControlLabel value='advanced' control={<Radio />} label='Show Advanced (for managers)' />
                </RadioGroup>
                <FormGroup row>
                    {'basic' === version ? (
                        <FormControlLabel
                            control={
                                <Checkbox checked={checkboxesState.trained} onChange={handleChange} name='trained' />
                            }
                            label='Trained'
                        />
                    ) : (
                        <>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={checkboxesState.trainedAsTraining}
                                        onChange={handleChange}
                                        name='trainedAsTraining'
                                    />
                                }
                                label='Trained as - Training'
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={checkboxesState.trainedAsValidation}
                                        onChange={handleChange}
                                        name='trainedAsValidation'
                                    />
                                }
                                label='Trained as - Validation'
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={checkboxesState.trainedAsTest}
                                        onChange={handleChange}
                                        name='trainedAsTest'
                                    />
                                }
                                label='Trained as - Test'
                            />
                        </>
                    )}

                    <FormControlLabel
                        control={
                            <Checkbox checked={checkboxesState.notTrained} onChange={handleChange} name='notTrained' />
                        }
                        label='Not Trained'
                    />
                    {isTagStore && (
                        <RadioGroup
                            row
                            value={isNegative ? 'negative' : 'positive'}
                            onChange={(event) => setisNegative('negative' === event.target.value)}
                        >
                            <FormControlLabel value='positive' control={<Radio />} label='Positive' />
                            <FormControlLabel value='negative' control={<Radio />} label='Negative' />
                        </RadioGroup>
                    )}
                </FormGroup>
            </Stack>
        </Stack>
    );
};
